import React, { useEffect, useState } from "react";
import TableComponent from "../../components/table/TableComponent";
import { MdAdd, MdEdit } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { DispatchType } from "../../interfaces/redux.interface";
import { FormControlLabel, Switch } from "@mui/material";
import { errorToast, successToast } from "../../utils/toast.util";
import { Column } from "../../interfaces/table.interface";
import { CouponInterface } from "../../interfaces/coupon.interface";
import { getCoupons, updateCoupon } from "../../redux/slices/CouponSlice";
import CreateCouponModal from "./CreateCouponModal";
import { RootState } from "../../redux/store";

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "string") return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message?: string }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

const isCouponExpired = (expiryDate: string | Date): boolean =>
  new Date(expiryDate) < new Date();

const CouponList = () => {
  const dispatch: DispatchType = useDispatch();
  const { couponList }: { couponList: CouponInterface[] } = useSelector(
    (state: RootState) => state.coupon
  );
  const [filteredCouponList, setFilteredCouponList] = useState<
    CouponInterface[]
  >([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editData, setEditData] = useState<CouponInterface>();
  const [isEdit, setIsEdit] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getCoupons())
      .unwrap()
      .catch((error) => {
        errorToast(
          getErrorMessage(error, "Failed to fetch coupons. Please try again.")
        );
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setFilteredCouponList(couponList);
  }, [couponList]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();

    const filteredList = couponList.filter((coupon) => {
      return (
        coupon._id?.toLowerCase().includes(lowerQuery) ||
        coupon.couponCode?.toLowerCase().includes(lowerQuery) ||
        coupon.discountType?.toLowerCase().includes(lowerQuery) ||
        coupon.couponFor?.toLowerCase().includes(lowerQuery) ||
        (coupon.discountValue?.toString().toLowerCase() || "").includes(
          lowerQuery
        ) ||
        coupon.expiryDate?.toLowerCase().includes(lowerQuery) ||
        (coupon.usageLimit?.toString().toLowerCase() || "").includes(
          lowerQuery
        ) ||
        (coupon.remainingUses?.toString().toLowerCase() || "").includes(
          lowerQuery
        ) ||
        (coupon.isActive ? "active" : "inactive").includes(lowerQuery) ||
        (coupon.isPublic ? "public" : "private").includes(lowerQuery) ||
        (isCouponExpired(coupon.expiryDate) && "expired".includes(lowerQuery))
      );
    });

    setFilteredCouponList(filteredList);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(undefined);
    setIsEdit(false);
  };

  const columns: Column[] = [
    { select: "couponCode", header: "Coupon Code", align: "left" },
    { select: "couponFor", header: "Coupon For" },
    { select: "discountValue", header: "Discount Value" },
    { select: "discountType", header: "Discount Type" },
    {
      select: "expiryDate",
      header: "Expiry Date",
      render: (_value: boolean, rowData: CouponInterface) => {
        const expired = isCouponExpired(rowData.expiryDate);
        return (
          <span>
            {new Date(rowData.expiryDate).toLocaleDateString("en-IN")}
            {expired && (
              <span className="ml-2 text-red-500 text-xs">Expired</span>
            )}
          </span>
        );
      },
    },
    { select: "usageLimit", header: "Usage Limit" },
    { select: "remainingUses", header: "Remaining Usage" },
    {
      select: "isPublic",
      header: "Public",
      render: (value: boolean, rowData: CouponInterface) => (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={value}
              onChange={(e) =>
                dispatch(
                  updateCoupon({
                    id: rowData._id,
                    data: {
                      isPublic: e.target.checked,
                      couponFor: rowData.couponFor,
                    },
                  })
                )
                  .unwrap()
                  .then(() => {
                    successToast("Public status updated successfully");
                  })
                  .catch((error) => {
                    errorToast(
                      getErrorMessage(
                        error,
                        "Failed to update public status. Please try again."
                      )
                    );
                  })
              }
            />
          }
          label={value ? "Yes" : "No"}
          labelPlacement="end"
        />
      ),
    },
    {
      select: "isActive",
      header: "Status",
      render: (value: boolean, rowData: CouponInterface) => {
        if (isCouponExpired(rowData.expiryDate)) {
          return <span className="text-red-500 text-sm">Expired</span>;
        }
        return (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={value}
                onChange={(e) =>
                  dispatch(
                    updateCoupon({
                      id: rowData._id,
                      data: { isActive: e.target.checked },
                    })
                  )
                    .unwrap()
                    .then(() => {
                      successToast("Status updated successfully");
                    })
                    .catch((error) => {
                      errorToast(
                        getErrorMessage(
                          error,
                          "Failed to update status. Please try again."
                        )
                      );
                    })
                }
              />
            }
            label="Active"
            labelPlacement="end"
          />
        );
      },
    },
    {
      header: "Edit",
      render: (_value: boolean, rowData: CouponInterface) => (
        <div
          className="rounded-full bg-slate-200 p-2 text-yellow-500 cursor-pointer"
          onClick={() => {
            setEditData(rowData);
            setIsEdit(true);
            setIsModalOpen(true);
          }}
        >
          <MdEdit className="m-auto" />
        </div>
      ),
    },
  ];

  return (
    <div className="coupon table-container">
      <div className="title">Coupon List</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search coupon by code, category, public/private, or discount value"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent columns={columns} data={filteredCouponList} />

      <div
        onClick={openModal}
        className="fixed top-8 right-8 bg-yellow-500 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-yellow-600"
      >
        <MdAdd />
      </div>

      <CreateCouponModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        isEdit={isEdit}
        editData={editData}
      />
    </div>
  );
};

export default CouponList;
